/**
 * UpgradeCard Component
 *
 * Shows Free users potential savings from upgrading to Pro.
 * Only displays when:
 * - User is on Free plan
 * - Has meaningful monthly activity (>= 3 bookings, >= $300 GMV)
 * - Estimated savings > $10/month
 *
 * Tracks analytics events for conversion funnel.
 */
import { useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp } from "lucide-react";
import { useUpgradeMetrics } from "@/hooks/useUpgradeMetrics";
import { useSubscription } from "@/hooks/useSubscription";

export function UpgradeCard() {
  const { metrics, isLoading } = useUpgradeMetrics();
  const { isUpgrading, upgradeError, upgradeToPro } = useSubscription();
  const hasTrackedView = useRef(false);

  // Track upgrade card view (analytics) - tracks each time card appears
  useEffect(() => {
    if (metrics?.show_upgrade_card && !hasTrackedView.current) {
      // Track that user saw the upgrade card
      if (typeof window !== 'undefined' && (window as any).posthog) {
        (window as any).posthog.capture('upgrade_card_viewed', {
          current_plan: metrics.current_plan,
          monthly_gmv: metrics.gmv,
          estimated_savings: metrics.savings_if_upgraded,
          booking_count: metrics.booking_count,
        });
        hasTrackedView.current = true;
      }
    } else if (!metrics?.show_upgrade_card) {
      // Reset tracking flag when card is hidden
      hasTrackedView.current = false;
    }
  }, [metrics]);

  // Don't show card if loading, no metrics, or guardrails not met
  if (isLoading || !metrics?.show_upgrade_card) {
    return null;
  }

  const handleUpgradeClick = () => {
    console.log('[UpgradeCard] Upgrade to Pro clicked');
    // Track upgrade button click
    if (typeof window !== 'undefined' && (window as any).posthog) {
      (window as any).posthog.capture('upgrade_clicked', {
        current_plan: metrics.current_plan,
        monthly_gmv: metrics.gmv,
        estimated_savings: metrics.savings_if_upgraded,
        booking_count: metrics.booking_count,
      });
    }

    upgradeToPro();
  };

  return (
    <Card className="border-figgy-500 bg-figgy-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrendingUp className="h-5 w-5 text-figgy-600" />
          Save money with Pro
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground space-y-1">
          <p>
            You paid{' '}
            <span className="font-semibold text-foreground">
              ${metrics.current_fees_paid.toFixed(2)}
            </span>{' '}
            in Figgy fees this month.
          </p>
          <p>
            On Pro, you would have paid about{' '}
            <span className="font-semibold text-foreground">
              ${metrics.projected_pro_total.toFixed(2)}
            </span>
            .
          </p>
          <p className="mt-2 text-base font-bold text-figgy-600">
            Save about ${metrics.savings_if_upgraded.toFixed(2)}/month with Pro.
          </p>
        </div>

        <Button
          onClick={handleUpgradeClick}
          disabled={isUpgrading}
          className="w-full bg-figgy-500 hover:bg-figgy-600 text-white"
        >
          {isUpgrading ? 'Starting checkout...' : 'Upgrade to Pro'}
        </Button>
        {upgradeError && (
          <p className="text-sm text-red-600 text-center">{upgradeError}</p>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Pro: $19/month + 0.75% Figgy fee + processing fees
        </p>
      </CardContent>
    </Card>
  );
}
